import { PrimaryButton } from "@digipost/dds-buttons-react";
import { Checkbox } from "@digipost/dds-checkbox-react";
import { Heading } from "@digipost/dds-heading-react";
import Infobox from "@digipost/dds-infobox-react";
import { Spinner } from "@digipost/dds-spinner-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  findURIWithWindowHostname,
  linkExist,
} from "../infrastructure/linkUtils";
import { Dispatch } from "../infrastructure/redux/actions";
import {
  completeIntegrationExistingVirksomhetAction,
  organisationAction,
} from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import Translated from "../infrastructure/Translated";
import { useRootPath } from "../infrastructure/useRootPath";
import { useTranslate } from "../infrastructure/useTranslate";
import { isAlreadyRegistered } from "./domain";
import styles from "./Fullmakt.module.css";
import RegisterFlowStepper from "./RegisterFlowStepper";

interface Props {
  isManuellRegistrering: boolean;
}

export const Fullmakt: FC<Props> = ({ isManuellRegistrering }) => {
  const translate = useTranslate();
  const dispatch = useDispatch<Dispatch>();
  const navigate = useNavigate();
  const rootPath = useRootPath();
  const {
    selectedOrganisationNumber,
    selectedIntegrationPartner: integrationPartner,
  } = useSelector((state: ReduxState) => state.org.userInput);
  const data = useSelector((state: ReduxState) => state.org.data);

  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [showNotAcceptedError, setShowNotAcceptedError] = useState(false);

  const handleCheckboxChange = (): void => {
    setAccepted(!accepted);
    setShowNotAcceptedError(false);
  };

  const fetchOrg = useCallback(
    () => dispatch(organisationAction()),
    [dispatch],
  );

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  if (!data) {
    return <Spinner size="large" className={styles.spinner} />;
  }

  const selectedOrganisation = !isManuellRegistrering
    ? data.registrableOrganisations.find(
        (p) => p.organisationNumber === selectedOrganisationNumber,
      )
    : undefined;

  const chosenOrgRegisteredInDigipost =
    selectedOrganisation && isAlreadyRegistered(selectedOrganisation);

  return (
    <div>
      <Heading level={1} size="large">
        <Translated textKey="register.org.fullmakt.title" />
      </Heading>

      <RegisterFlowStepper currentStep="POWER_OF_ATTOURNEY" />

      <div className={styles.container}>
        <div>
          <Heading level={2} size={"small"}>
            <Translated textKey="register.org.fullmakt.system_provider" />
          </Heading>
          {integrationPartner && (
            <div className={styles.borderBox}>
              <img
                className={styles.integrationPartnerLogo}
                src={
                  linkExist("organisation_logo", integrationPartner.link)
                    ? findURIWithWindowHostname(
                        "organisation_logo",
                        integrationPartner.link,
                      )
                    : ""
                }
                alt={`Logo ${integrationPartner.name}`}
              />
              <p>{integrationPartner.name}</p>
            </div>
          )}
        </div>
        <p>
          <Translated
            textKey="register.org.fullmakt.infotext"
            params={{
              systemleverandoer: integrationPartner?.name,
              virksomhet:
                selectedOrganisation?.name ??
                translate("register.org.fullmakt.your_org"),
            }}
          />
        </p>
        <div className={styles.fullmaktCheckbox}>
          <Checkbox
            checked={accepted}
            onChange={handleCheckboxChange}
            data-testid="bedrift-fullmakt-checkbox"
          >
            <Translated
              textKey="register.org.fullmakt.checkbox"
              params={{
                systemleverandoer: integrationPartner?.name,
                virksomhet:
                  selectedOrganisation?.name ??
                  translate("register.org.fullmakt.my_org"),
              }}
            />
          </Checkbox>
        </div>
        <div aria-live="assertive">
          {showNotAcceptedError && (
            <Infobox
              level={2}
              title={translate("register.org.fullmakt.checkbox_error")}
              variant="warning"
            />
          )}
          {error && <Infobox level={2} title={error.message} variant="error" />}
        </div>
        <PrimaryButton
          disabled={loading}
          iconRight={loading ? <Spinner /> : null}
          onClick={async () => {
            if (accepted && chosenOrgRegisteredInDigipost) {
              setLoading(true);

              try {
                await dispatch(
                  completeIntegrationExistingVirksomhetAction({
                    organisationNumber: selectedOrganisation.organisationNumber,
                    integrationPartnerId: integrationPartner?.senderId
                      ? Number(integrationPartner.senderId)
                      : undefined,
                  }),
                );

                navigate(`${rootPath}/bedrift/iconfirm`);
              } catch (err) {
                setError(err as Error);
              } finally {
                setLoading(false);
              }
            } else if (accepted) {
              navigate("../avtalevilkaar");
            } else {
              setShowNotAcceptedError(true);
            }
          }}
          className={styles.continueButton}
          data-testid="bedrift-fullmakt-next-button"
        >
          <Translated textKey="generic.continue" />
        </PrimaryButton>
      </div>
    </div>
  );
};
